import React, { Component } from 'react';
import { observable, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FeatherIcon from 'feather-icons-react';
import { Button, Badge } from 'shards-react';

import './style.scss';
import PageTitle from '../../../components/common/PageTitle';
import ModalLoading from '../../../components/common/ModalLoading';

const DEFAULT_CONFIG = {
    displayGroup: null,      // 대상 진열 그룹 — 마지막 단계에서 선택
    isUse: false,            // 자동 적용 ON/OFF (frequency 와 함께 사용)
    frequency: 'daily',      // 'daily' | 'weekly'
    replaceMode: 'replace',  // 'replace' | 'prepend'
    topN: 10,
    periodDays: 14,
    weights: { order: 60, review: 25, rating: 15 },
};

const WEIGHT_FIELDS = [
    { key: 'order',  label: '주문수',    icon: 'shopping-cart' },
    { key: 'review', label: '리뷰수',    icon: 'message-square' },
    { key: 'rating', label: '평균 별점', icon: 'star' },
];

const PERIOD_PRESETS = [
    { days: 7,  label: '최근 1주' },
    { days: 14, label: '최근 2주' },
    { days: 30, label: '최근 1개월' },
    { days: 90, label: '최근 3개월' },
];

const AUTO_OPTIONS = [
    { key: 'off',    label: '사용 안 함' },
    { key: 'daily',  label: '매일' },
    { key: 'weekly', label: '매주 (월요일)' },
];

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ design }) => ({ getMainDisplayGroup: design.getMainDisplayGroup }))
@inject(({ common }) => ({ getDoc: common.getDoc, updateDoc: common.updateDoc }))
@inject(({ booster }) => ({
    getPopularProducts: booster.getPopularProducts,
    applyPopularDisplay: booster.applyPopularDisplay,
}))
@observer
class PopularDisplay extends Component {

    // basic
    @observable store = {};
    @observable teamUser = {};
    @observable isLogin = false;
    @observable isLoading = false;

    // 진열 그룹 (마지막 단계 선택용)
    @observable groups = [];
    @observable groupsError = '';
    @observable groupsLoaded = false;

    // 설정
    @observable config = { ...DEFAULT_CONFIG, weights: { ...DEFAULT_CONFIG.weights } };
    @observable savedSnapshot = '';   // dirty 판정용
    @observable lastAppliedAt = null;
    @observable lastResult = null;
    @observable lastRunBy = '';

    // 인기 상품 미리보기 (그룹과 무관 — 기간 변경 시만 서버 재호출, 가중치/개수는 즉시 재계산)
    @observable rawItems = [];
    @observable ranking = { loaded: false, isLoading: false, error: '' };

    // 적용 영향 (그룹 선택 시 dryRun 으로 신규/제거/고정보호 요약)
    @observable impact = { plan: null, loaded: false, isLoading: false, error: '' };
    _impactTimer = null;

    constructor(props) {
        super(props);
        this.init();
    }

    @action
    init = async () => {
        if (this.props.isLogin) {
            this.isLogin = this.props.isLogin;
            this.teamUser = this.props.me;
            this.store = this.props.me.store ? this.props.me.store : {};
            this.fetchGroups();
            this.fetchConfig();
            this.fetchRanking();
        }
    };

    baseParams = () => ({
        store_id: this.teamUser.selStore_id,
        shopNo: this.teamUser.selShopNo || 1,
    });

    // cafe24 프록시 응답 공통 에러 추출 (insufficient_scope 친화 메시지 포함)
    extractError = (res) => {
        let msg = null;
        if (res && res.errors && res.errors.length > 0) {
            msg = res.errors[0].message || '요청에 실패했습니다.';
        }
        else {
            const raw = res && res.data ? res.data : null;
            if (raw && raw.error) {
                msg = raw.error.message || '카페24 응답 오류가 발생했습니다.';
                if (raw.error.code) msg += ` (${raw.error.code})`;
            }
        }
        if (msg && (msg.indexOf('insufficient_scope') > -1 || msg.indexOf('permission necessary') > -1)) {
            return '카페24 앱에 메인진열 변경 권한이 없습니다. 카페24 관리자에서 온리플 앱을 다시 실행해 새 권한(카테고리/진열 쓰기)에 동의하면 사용할 수 있습니다.';
        }
        return msg;
    };

    // ===== 진열 그룹 (마지막 단계) =====
    @action
    fetchGroups = () => {
        let _this = this;
        this.groupsError = '';

        this.props.getMainDisplayGroup(this.baseParams(), action(function (res) {
            _this.groupsLoaded = true;
            const err = _this.extractError(res);
            if (err) {
                _this.groupsError = err;
                return;
            }
            const raw = res && res.data ? res.data : {};
            _this.groups = (raw && raw.mains) ? raw.mains : [];
            // 그룹은 사용자가 마지막 단계에서 직접 선택 — 자동 선택하지 않음 (저장된 설정만 반영)
            if (_this.config.displayGroup) _this.scheduleImpactRefresh(true);
        }));
    };

    // ===== 설정 로드/저장 =====
    configSnapshot = () => {
        const c = this.config;
        return JSON.stringify({
            displayGroup: c.displayGroup, isUse: c.isUse, frequency: c.frequency,
            replaceMode: c.replaceMode, topN: c.topN, periodDays: c.periodDays,
            weights: { order: c.weights.order, review: c.weights.review, rating: c.weights.rating }
        });
    };

    @computed
    get isDirty() {
        if (!this.savedSnapshot) return false;
        return this.configSnapshot() !== this.savedSnapshot;
    }

    @action
    fetchConfig = () => {
        let _this = this;

        this.props.getDoc('getPopularDisplayConfig', this.baseParams(), action(function (res) {
            const doc = res && res.data ? res.data : null;
            if (doc) {
                _this.config = {
                    displayGroup: doc.displayGroup || null,
                    isUse: !!doc.isUse,
                    frequency: doc.frequency || 'daily',
                    replaceMode: doc.replaceMode || 'replace',
                    topN: doc.topN || 10,
                    periodDays: doc.periodDays || 14,
                    weights: Object.assign({ ...DEFAULT_CONFIG.weights }, doc.weights || {}),
                };
                _this.lastAppliedAt = doc.lastAppliedAt || null;
                _this.lastResult = doc.lastResult || null;
                _this.lastRunBy = doc.lastRunBy || '';
            }
            _this.savedSnapshot = _this.configSnapshot();
            // 저장된 기간이 기본값과 다르면 랭킹 재조회
            _this.fetchRanking();
            if (_this.config.displayGroup) _this.scheduleImpactRefresh(true);
        }));
    };

    @action
    saveConfig = () => {
        let _this = this;
        if (!this.config.displayGroup) {
            toast('대상 진열 그룹을 선택해 주세요.');
            return;
        }
        this.isLoading = true;

        const param = {
            ...this.baseParams(),
            displayGroup: this.config.displayGroup,
            isUse: this.config.isUse,
            frequency: this.config.frequency,
            replaceMode: this.config.replaceMode,
            topN: this.config.topN,
            periodDays: this.config.periodDays,
            weights: { ...this.config.weights },
        };

        this.props.updateDoc('updatePopularDisplayConfig', param, action(function (res, errors) {
            _this.isLoading = false;
            if (errors && errors.length > 0) {
                toast.error(errors[0].message || '설정 저장에 실패했습니다.');
                return;
            }
            toast.success('인기상품 메인진열 설정이 저장되었습니다.');
            _this.savedSnapshot = _this.configSnapshot();
        }));
    };

    @action
    revertConfig = () => {
        if (!this.savedSnapshot) return;
        const saved = JSON.parse(this.savedSnapshot);
        const prevPeriod = this.config.periodDays;
        this.config = { ...saved, weights: { ...saved.weights } };
        if (this.config.periodDays !== prevPeriod) this.fetchRanking();
        this.scheduleImpactRefresh();
    };

    // ===== 설정 변경 핸들러 =====
    @action setTopN = (n) => { this.config.topN = n; this.scheduleImpactRefresh(); };
    @action setPeriod = (days) => {
        if (this.config.periodDays === days) return;
        this.config.periodDays = days;
        this.fetchRanking(); // 기간 변경만 서버 재호출
        this.scheduleImpactRefresh();
    };
    @action setWeight = (key, value) => { this.config.weights[key] = parseInt(value, 10); this.scheduleImpactRefresh(); };
    @action resetWeights = () => { this.config.weights = { ...DEFAULT_CONFIG.weights }; this.scheduleImpactRefresh(); };
    @action setReplaceMode = (mode) => { this.config.replaceMode = mode; this.scheduleImpactRefresh(); };
    @action selectGroup = (dg) => { this.config.displayGroup = dg; this.scheduleImpactRefresh(true); };

    @action
    setAuto = (key) => {
        if (key === 'off') {
            this.config.isUse = false;
        } else {
            this.config.isUse = true;
            this.config.frequency = key;
        }
    };

    weightShare = (key) => {
        const w = this.config.weights;
        const sum = (w.order || 0) + (w.review || 0) + (w.rating || 0);
        if (sum === 0) return 0;
        return Math.round((w[key] / sum) * 100);
    };

    // ===== 인기 상품 미리보기 (getPopularProducts — 그룹 불필요) =====
    @action
    fetchRanking = () => {
        let _this = this;
        this.ranking.isLoading = true;
        this.ranking.error = '';

        this.props.getPopularProducts({
            ...this.baseParams(),
            startDate: moment().subtract(this.config.periodDays - 1, 'days').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            fillTopN: 30, // 진열 개수 최대값 — 기간 내 상품이 부족하면 전체기간 리뷰 기준으로 채움
        }, action(function (res) {
            _this.ranking.isLoading = false;
            _this.ranking.loaded = true;
            if (res && res.errors && res.errors.length > 0) {
                _this.ranking.error = res.errors[0].message || '인기 상품을 불러오지 못했습니다.';
                _this.rawItems = [];
                return;
            }
            const data = res && res.data ? res.data : {};
            _this.rawItems = data.items || [];
        }));
    };

    // 그룹 내 정규화 + 가중합 점수 (서버 fn.scorePopularItems 와 동일 공식)
    scoreGroup = (items) => {
        if (!items || items.length === 0) return [];
        const w = this.config.weights;
        const wSum = (w.order || 0) + (w.review || 0) + (w.rating || 0);
        const maxOrder = Math.max(...items.map(it => it.orderCount || 0), 0);
        const maxReview = Math.max(...items.map(it => it.reviewCount || 0), 0);
        const maxRating = Math.max(...items.map(it => it.avgGoodsPt || 0), 0);

        const scored = items.map(it => {
            const nO = maxOrder > 0 ? (it.orderCount || 0) / maxOrder : 0;
            const nR = maxReview > 0 ? (it.reviewCount || 0) / maxReview : 0;
            const nG = maxRating > 0 ? (it.avgGoodsPt || 0) / maxRating : 0;
            const score = wSum > 0 ? ((w.order * nO + w.review * nR + w.rating * nG) / wSum) * 100 : 0;
            return { ...it, score: Math.round(score * 10) / 10 };
        });
        scored.sort((a, b) => b.score - a.score);
        return scored;
    };

    // 랭킹 — 서버 fn.computePopularRanking 과 동일 규칙:
    // 기간 내 상품(주문∪리뷰) 점수순 + 부족분은 전체기간 리뷰 폴백(점수순)을 뒤에 채워 진열 개수를 맞춤
    @computed
    get rankedItems() {
        const items = (this.rawItems || []).filter(it => it.platformProductId !== null && it.platformProductId !== undefined);
        if (items.length === 0) return [];

        const periodItems = this.scoreGroup(items.filter(it => !it.isPeriodFallback));
        const fallbackItems = this.scoreGroup(items.filter(it => it.isPeriodFallback));

        return [...periodItems, ...fallbackItems].slice(0, this.config.topN);
    }

    // ===== 적용 영향 (그룹 선택 시 dryRun 요약) =====
    scheduleImpactRefresh = (immediate) => {
        if (this._impactTimer) clearTimeout(this._impactTimer);
        if (!this.config.displayGroup) {
            this.impact = { plan: null, loaded: false, isLoading: false, error: '' };
            return;
        }
        this._impactTimer = setTimeout(() => {
            this._impactTimer = null;
            this.fetchImpact();
        }, immediate ? 0 : 600);
    };

    @action
    fetchImpact = () => {
        let _this = this;
        if (!this.config.displayGroup) return;
        this.impact.isLoading = true;
        this.impact.error = '';

        this.props.applyPopularDisplay({
            ...this.baseParams(),
            dryRun: true,
            configOverride: this.configForApply(),
        }, action(function (res) {
            _this.impact.isLoading = false;
            _this.impact.loaded = true;
            const err = _this.extractError(res);
            if (err) {
                _this.impact.error = err;
                _this.impact.plan = null;
                return;
            }
            _this.impact.plan = res && res.data ? res.data : null;
        }));
    };

    // configOverride 용 plain 객체
    configForApply = () => ({
        displayGroup: this.config.displayGroup,
        replaceMode: this.config.replaceMode,
        topN: this.config.topN,
        periodDays: this.config.periodDays,
        weights: { ...this.config.weights },
    });

    // ===== 적용 =====
    @action
    applyNow = () => {
        let _this = this;
        if (!this.config.displayGroup) {
            toast('대상 진열 그룹을 선택해 주세요.');
            return;
        }
        const g = this.groups.find(g => g.display_group === this.config.displayGroup);
        const gName = g ? (g.group_name || g.name || `그룹 #${g.display_group}`) : `그룹 #${this.config.displayGroup}`;
        const modeText = this.config.replaceMode === 'prepend' ? '상위 배치' : '완전 교체';
        if (!window.confirm(`[${gName}] 진열을 현재 설정(인기 TOP ${this.config.topN}, ${modeText})으로 지금 적용하시겠습니까?\n쇼핑몰에 바로 반영됩니다.`)) return;

        this.isLoading = true;
        this.props.applyPopularDisplay({
            ...this.baseParams(),
            dryRun: false,
            configOverride: this.configForApply(),
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            const plan = res && res.data ? res.data : {};
            toast.success(`진열이 적용되었습니다. (추가 ${plan.added ? plan.added.length : 0} · 제거 ${plan.removed ? plan.removed.length : 0} · 총 ${plan.finalOrder ? plan.finalOrder.length : 0}개 구성)`);
            _this.impact.plan = plan;
            _this.impact.loaded = true;
            _this.impact.error = '';
            _this.fetchConfig(); // lastAppliedAt/lastResult 갱신
        }));
    };

    renderStars = (avgGoodsPt) => {
        if (!avgGoodsPt) return <span className="pp-rating pp-rating-none">-</span>;
        const star5 = Math.round((avgGoodsPt / 20) * 10) / 10;
        return (
            <span className="pp-rating">
                <FeatherIcon icon="star" size="12" className="pp-star-icon" aria-hidden="true" />
                {star5.toFixed(1)}
            </span>
        );
    };

    render() {
        const groupName = (g) => g.group_name || g.name || `진열 그룹 ${g.display_group}`;
        const ranked = this.rankedItems;
        const plan = this.impact.plan;
        const autoKey = this.config.isUse ? this.config.frequency : 'off';
        const noGroup = !this.config.displayGroup;   // 대상 진열 그룹 미선택 시 이후 설정(구성 방식·자동 적용) 잠금

        return (
            <div className="main-content main-display-page popular-display-page">
                <PageTitle />

                {(this.isLogin && this.teamUser.emailId) ? (
                    <div className="md-container">

                        {/* 안내 */}
                        <div className="md-guide">
                            <FeatherIcon icon="info" size="14" aria-hidden="true" />
                            <span>
                                <strong>① 랭킹 기준 설정 → ② 인기 상품 미리보기 → ③ 대상 진열 그룹 선택 후 적용</strong> 순서로 진행합니다.
                                자동 적용(매일/매주 새벽 5:30)을 켜 두면 선택한 그룹이 주기적으로 인기 순위대로 갱신됩니다.
                            </span>
                        </div>

                        {/* ===== ① 랭킹 기준 설정 ===== */}
                        <div className="pd-card">
                            <div className="pd-card-header">
                                <span className="pd-step-no">1</span>
                                <strong>랭킹 기준 설정</strong>
                                <span className="pd-header-sub">어떤 기준으로 인기 상품을 뽑을지 설정합니다</span>
                            </div>

                            <div className="pd-row pd-row-split">
                                <div className="pd-row-half">
                                    <span className="pd-row-label"><FeatherIcon icon="bar-chart-2" size="13" aria-hidden="true" /> 진열 개수</span>
                                    <div className="pp-presets">
                                        {[10, 15, 20, 30].map(n => (
                                            <button key={n} type="button"
                                                    role="radio"
                                                    aria-checked={this.config.topN === n}
                                                    className={`pp-preset ${this.config.topN === n ? 'is-active' : ''}`}
                                                    onClick={() => this.setTopN(n)}>
                                                TOP {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="pd-row-half">
                                    <span className="pd-row-label"><FeatherIcon icon="calendar" size="13" aria-hidden="true" /> 분석 기간</span>
                                    <div className="pp-presets">
                                        {PERIOD_PRESETS.map(p => (
                                            <button key={p.days} type="button"
                                                    role="radio"
                                                    aria-checked={this.config.periodDays === p.days}
                                                    className={`pp-preset ${this.config.periodDays === p.days ? 'is-active' : ''}`}
                                                    onClick={() => this.setPeriod(p.days)}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="pd-row">
                                <span className="pd-row-label">
                                    <FeatherIcon icon="sliders" size="13" aria-hidden="true" /> 랭킹 가중치
                                    <button type="button" className="pp-reset-btn pd-weights-reset"
                                            onClick={() => this.resetWeights()}
                                            title="기본값(주문 60 / 리뷰 25 / 평점 15)으로 되돌리기">
                                        <FeatherIcon icon="rotate-ccw" size="11" aria-hidden="true" /> 기본값
                                    </button>
                                </span>
                                <div className="pd-weights-grid">
                                    {WEIGHT_FIELDS.map(f => (
                                        <div key={f.key} className="pp-weight-row">
                                            <span className="pp-weight-label">
                                                <FeatherIcon icon={f.icon} size="13" aria-hidden="true" />
                                                {f.label}
                                            </span>
                                            <input type="range" min="0" max="100" step="5"
                                                   className="pp-weight-slider"
                                                   value={this.config.weights[f.key]}
                                                   onChange={(e) => this.setWeight(f.key, e.target.value)}
                                                   aria-label={`${f.label} 가중치`} />
                                            <span className="pp-weight-value">{this.config.weights[f.key]}</span>
                                            <span className="pp-weight-share">비중 {this.weightShare(f.key)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ===== ② 인기 상품 미리보기 (그룹 무관) ===== */}
                        <div className="pd-card">
                            <div className="pd-card-header">
                                <span className="pd-step-no">2</span>
                                <strong>인기 상품 미리보기</strong>
                                <span className="pd-header-sub">위 기준으로 산출된 인기 순위입니다 — 이 순서대로 진열됩니다</span>
                                {this.ranking.isLoading && (
                                    <span className="pd-list-loading">
                                        <FeatherIcon icon="loader" size="12" aria-hidden="true" /> 갱신 중...
                                    </span>
                                )}
                                <span className="pd-preview-summary">
                                    {this.ranking.loaded && !this.ranking.error && (
                                        <>총 <strong>{ranked.length}개</strong></>
                                    )}
                                    <button type="button" className="pp-reset-btn"
                                            onClick={() => this.fetchRanking()}
                                            title="인기 상품 다시 조회">
                                        <FeatherIcon icon="refresh-cw" size="11" aria-hidden="true" /> 새로고침
                                    </button>
                                </span>
                            </div>

                            {this.ranking.error && (
                                <div className="md-props-error">
                                    <FeatherIcon icon="alert-triangle" size="13" aria-hidden="true" />
                                    <span>{this.ranking.error}</span>
                                    <Button theme="light" size="sm" onClick={() => this.fetchRanking()}>다시 시도</Button>
                                </div>
                            )}

                            {!this.ranking.error && !this.ranking.loaded && (
                                <div className="pd-list-placeholder">
                                    <FeatherIcon icon="loader" size="20" aria-hidden="true" />
                                    <span>인기 상품을 불러오는 중입니다...</span>
                                </div>
                            )}

                            {!this.ranking.error && this.ranking.loaded && ranked.length === 0 && (
                                <div className="pd-list-placeholder">
                                    <FeatherIcon icon="inbox" size="20" aria-hidden="true" />
                                    <span>선택한 기간에 주문 데이터가 없습니다 — 분석 기간을 늘려 보세요.</span>
                                </div>
                            )}

                            {!this.ranking.error && ranked.length > 0 && (
                                <div className={`md-table-wrap ${this.ranking.isLoading ? 'pd-table-dim' : ''}`}>
                                    <table className="md-table">
                                        <colgroup>
                                            <col width="60" />
                                            <col />
                                            <col width="85" />
                                            <col width="85" />
                                            <col width="95" />
                                            <col width="95" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className="text-center">순위</th>
                                                <th>상품</th>
                                                <th className="text-right">주문수</th>
                                                <th className="text-right">리뷰수</th>
                                                <th className="text-right">평균 별점</th>
                                                <th className="text-right">종합점수</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ranked.map((item, idx) => (
                                                <tr key={item.product_id} className={idx < 3 ? `pp-row-top pp-row-top-${idx + 1}` : ''}>
                                                    <td className="text-center">
                                                        <span className={`pp-rank ${idx < 3 ? `pp-rank-${idx + 1}` : ''}`}>{idx + 1}</span>
                                                    </td>
                                                    <td>
                                                        <div className="pp-product">
                                                            {item.image ? (
                                                                <img className="pp-product-img" src={item.image} alt={item.productName} />
                                                            ) : (
                                                                <div className="pp-product-img pp-product-img-empty">
                                                                    <FeatherIcon icon="image" size="14" aria-hidden="true" />
                                                                </div>
                                                            )}
                                                            <div className="pp-product-info">
                                                                <div className="pp-product-name" title={item.productName}>
                                                                    {item.productName}
                                                                    {item.isPeriodFallback && (
                                                                        <span className="pd-fallback-badge" title="선택한 기간에 주문/리뷰가 부족해 전체기간 리뷰수·평점 기준으로 채워진 상품입니다">
                                                                            전체기간 리뷰 기준
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="pp-product-meta">
                                                                    <span>상품번호 {item.platformProductId}</span>
                                                                    {item.price ? <span>{item.price.toLocaleString()}원</span> : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-right"><strong>{(item.orderCount || 0).toLocaleString()}</strong></td>
                                                    <td className="text-right">{(item.reviewCount || 0).toLocaleString()}</td>
                                                    <td className="text-right">{this.renderStars(item.avgGoodsPt)}</td>
                                                    <td className="text-right"><span className="pd-score">{item.score.toFixed(1)}</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* ===== ③ 대상 진열 그룹 선택 + 적용 (마지막 단계) ===== */}
                        <div className="pd-card">
                            <div className="pd-card-header">
                                <span className="pd-step-no">3</span>
                                <strong>적용 대상 / 방식</strong>
                                <span className="pd-header-sub">위 인기 상품을 어느 진열 그룹에 어떻게 적용할지 선택합니다</span>
                                {this.isDirty && <Badge theme="warning" className="pd-dirty-badge">저장되지 않음</Badge>}
                            </div>

                            {/* 대상 그룹 (마지막 선택) */}
                            <div className="pd-row">
                                <span className="pd-row-label"><FeatherIcon icon="grid" size="13" aria-hidden="true" /> 대상 진열 그룹</span>
                                <div className="md-groups">
                                    {this.groups.map(g => (
                                        <button key={g.display_group} type="button"
                                                role="radio"
                                                aria-checked={this.config.displayGroup === g.display_group}
                                                className={`md-group-chip ${this.config.displayGroup === g.display_group ? 'is-active' : ''}`}
                                                onClick={() => this.selectGroup(g.display_group)}>
                                            {groupName(g)}
                                            <span className="md-group-no">#{g.display_group}</span>
                                        </button>
                                    ))}
                                    {this.groupsLoaded && this.groups.length === 0 && !this.groupsError && (
                                        <span className="md-muted">진열 그룹이 없습니다 — 메인 상품 진열에서 추가하세요</span>
                                    )}
                                    {this.groupsError && <span className="md-muted">{this.groupsError}</span>}
                                </div>
                                {noGroup && this.groupsLoaded && this.groups.length > 0 && (
                                    <span className="pd-group-hint">
                                        <FeatherIcon icon="arrow-up" size="11" aria-hidden="true" /> 적용할 그룹을 먼저 선택해야 아래 구성 방식 · 자동 적용을 설정하고 적용할 수 있습니다
                                    </span>
                                )}
                            </div>

                            {/* 적용 영향 요약 (그룹 선택 시 dryRun) */}
                            {this.config.displayGroup && (
                                <div className="pd-row">
                                    <span className="pd-row-label"><FeatherIcon icon="activity" size="13" aria-hidden="true" /> 적용 영향</span>
                                    {this.impact.isLoading && (
                                        <span className="pd-list-loading">
                                            <FeatherIcon icon="loader" size="12" aria-hidden="true" /> 현재 진열과 비교 중...
                                        </span>
                                    )}
                                    {!this.impact.isLoading && this.impact.error && (
                                        <div className="md-props-error">
                                            <FeatherIcon icon="alert-triangle" size="13" aria-hidden="true" />
                                            <span>{this.impact.error}</span>
                                            <Button theme="light" size="sm" onClick={() => this.scheduleImpactRefresh(true)}>다시 시도</Button>
                                        </div>
                                    )}
                                    {!this.impact.isLoading && !this.impact.error && plan && (
                                        <div className="pd-impact">
                                            <div className="pd-impact-badges">
                                                <span>총 <strong>{plan.finalOrder ? plan.finalOrder.length : 0}개</strong>로 구성</span>
                                                <Badge theme="success" className="pd-runby-badge">신규 추가 {(plan.added || []).length}</Badge>
                                                {plan.mode === 'replace' && <Badge theme="danger" className="pd-runby-badge">제거 {(plan.removed || []).length}</Badge>}
                                                {(plan.protectedFix || []).length > 0 && <Badge theme="warning" className="pd-runby-badge">고정 보호 {(plan.protectedFix || []).length}</Badge>}
                                                <span className="md-muted">(현재 진열 {plan.currentCount || 0}개 기준)</span>
                                            </div>
                                            {plan.mode === 'replace' && (plan.removed || []).length > 0 && (
                                                <div className="pd-plan-note pd-note-remove">
                                                    <FeatherIcon icon="trash-2" size="12" aria-hidden="true" />
                                                    <span>적용 시 제거됨 (순위 밖 비고정 상품):</span>
                                                    {(plan.removed || []).map(n => <code key={n}>{n}</code>)}
                                                </div>
                                            )}
                                            {(plan.protectedFix || []).length > 0 && (
                                                <div className="pd-plan-note pd-note-fix">
                                                    <FeatherIcon icon="lock" size="12" aria-hidden="true" />
                                                    <span>고정 상품 보호 (순위 밖이지만 유지):</span>
                                                    {(plan.protectedFix || []).map(n => <code key={n}>{n}</code>)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 구성 방식 — 대상 그룹 선택 후 활성화 */}
                            <div className={`pd-row ${noGroup ? 'pd-row-locked' : ''}`}>
                                <span className="pd-row-label">
                                    <FeatherIcon icon="layers" size="13" aria-hidden="true" /> 구성 방식
                                    {noGroup && <span className="pd-lock-tag"><FeatherIcon icon="lock" size="10" aria-hidden="true" /> 그룹 선택 필요</span>}
                                </span>
                                <div className="pd-mode-options">
                                    <button type="button"
                                            role="radio"
                                            disabled={noGroup}
                                            aria-checked={this.config.replaceMode === 'replace'}
                                            className={`pd-mode-option ${this.config.replaceMode === 'replace' ? 'is-active' : ''}`}
                                            onClick={() => this.setReplaceMode('replace')}>
                                        <strong>완전 교체</strong>
                                        <span>그룹을 인기 TOP{this.config.topN}으로만 구성합니다. 순위 밖 기존 상품은 진열에서 제거됩니다. (고정 상품은 항상 유지)</span>
                                    </button>
                                    <button type="button"
                                            role="radio"
                                            disabled={noGroup}
                                            aria-checked={this.config.replaceMode === 'prepend'}
                                            className={`pd-mode-option ${this.config.replaceMode === 'prepend' ? 'is-active' : ''}`}
                                            onClick={() => this.setReplaceMode('prepend')}>
                                        <strong>상위 배치</strong>
                                        <span>인기 TOP{this.config.topN}을 진열 맨 위로 배치하고, 기존 상품은 아래에 그대로 유지합니다. (삭제 없음)</span>
                                    </button>
                                </div>
                            </div>

                            {/* 자동 적용 — 대상 그룹 선택 후 활성화 */}
                            <div className={`pd-row ${noGroup ? 'pd-row-locked' : ''}`}>
                                <span className="pd-row-label">
                                    <FeatherIcon icon="repeat" size="13" aria-hidden="true" /> 자동 적용
                                    {noGroup && <span className="pd-lock-tag"><FeatherIcon icon="lock" size="10" aria-hidden="true" /> 그룹 선택 필요</span>}
                                </span>
                                <div className="pd-auto-wrap">
                                    <div className="pp-presets">
                                        {AUTO_OPTIONS.map(o => (
                                            <button key={o.key} type="button"
                                                    role="radio"
                                                    disabled={noGroup}
                                                    aria-checked={autoKey === o.key}
                                                    className={`pp-preset ${autoKey === o.key ? 'is-active' : ''}`}
                                                    onClick={() => this.setAuto(o.key)}>
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="pd-auto-hint">
                                        {this.config.isUse
                                            ? `${this.config.frequency === 'weekly' ? '매주 월요일' : '매일'} 새벽 5:30에 자동으로 진열을 갱신합니다`
                                            : '자동 적용이 꺼져 있습니다 — 수동 적용만 가능합니다'}
                                    </span>
                                </div>
                            </div>

                            {/* 적용 액션 */}
                            <div className="pd-card-footer">
                                <div className="pd-footer-left">
                                    {this.lastAppliedAt && (
                                        <span className="pd-last-applied">
                                            <FeatherIcon icon="clock" size="12" aria-hidden="true" />
                                            마지막 적용 {moment(this.lastAppliedAt).format('YYYY.MM.DD HH:mm')}
                                            {this.lastRunBy && <Badge theme="light" className="pd-runby-badge">{this.lastRunBy === 'auto' ? '자동' : '수동'}</Badge>}
                                            {this.lastResult && this.lastResult.ok === false && (
                                                <Badge theme="danger" className="pd-runby-badge" title={this.lastResult.error}>실패</Badge>
                                            )}
                                            {this.lastResult && this.lastResult.ok === true && (
                                                <span className="pd-last-summary">
                                                    추가 {(this.lastResult.added || []).length} · 제거 {(this.lastResult.removed || []).length}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div className="pd-footer-actions">
                                    {this.isDirty && (
                                        <Button theme="light" size="sm" onClick={() => this.revertConfig()}>되돌리기</Button>
                                    )}
                                    <Button theme="light" size="sm" onClick={() => this.saveConfig()}>
                                        <FeatherIcon icon="save" size="12" aria-hidden="true" /> 설정 저장
                                    </Button>
                                    <Button theme="primary" size="sm"
                                            disabled={!this.config.displayGroup}
                                            onClick={() => this.applyNow()}>
                                        <FeatherIcon icon="zap" size="12" aria-hidden="true" /> 지금 적용
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                ) : null}

                <ModalLoading isLoading={this.isLoading} />
            </div>
        );
    }
}

export default PopularDisplay;
